import React, { useEffect, useState } from 'react';
import { orderService, Order } from '../services/orderService';
import { appointmentService, Appointment } from '../services/appointmentService';
import { formatCurrency } from '../utils';
import { ShoppingBag, Calendar, CreditCard, Package, TrendingUp, Clock, CheckCircle, XCircle, Filter } from 'lucide-react';

interface Props {
  branch?: 'antofagasta' | 'santiago';
}

const SalesDashboard: React.FC<Props> = ({ branch = 'antofagasta' }) => {
  const [activeTab, setActiveTab] = useState<'products' | 'services' | 'deposits' | 'subscriptions' | 'deleted'>('products');
  const [orders, setOrders] = useState<Order[]>([]);
  const [deletedOrders, setDeletedOrders] = useState<Order[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [changingStatusId, setChangingStatusId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadingData, setReloadingData] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
  const [showEmptyTrashModal, setShowEmptyTrashModal] = useState(false);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [deletedCurrentPage, setDeletedCurrentPage] = useState(1);
  
  // Filtros
  const [filterStatus, setFilterStatus] = useState<Order['status'] | 'all'>('all');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [branch]);

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterDateFrom, filterDateTo]);

  const loadData = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      console.log('[SalesDashboard] Iniciando carga de datos...');
      const [ordersData, appointmentsData, deletedOrdersData] = await Promise.all([
        orderService.getAll(),
        branch === 'santiago' ? appointmentService.getAllBookingsSantiago() : appointmentService.getAllBookings(),
        orderService.getDeleted()
      ]);
      console.log('[SalesDashboard] Órdenes recibidas:', ordersData);
      console.log('[SalesDashboard] Cantidad de órdenes:', ordersData?.length || 0);
      console.log('[SalesDashboard] Órdenes eliminadas:', deletedOrdersData?.length || 0);
      console.log('[SalesDashboard] Appointments recibidos:', appointmentsData?.length || 0);
      console.log('[SalesDashboard] Branch actual:', branch);
      console.log('[SalesDashboard] Appointments completos:', appointmentsData);
      setOrders(ordersData);
      setDeletedOrders(deletedOrdersData);
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('[SalesDashboard] Error loading sales data:', error);
      // Mostrar detalles del error
      if (error instanceof Error) {
        console.error('[SalesDashboard] Error message:', error.message);
      }
      if ((error as any)?.response) {
        console.error('[SalesDashboard] Response status:', (error as any).response.status);
        console.error('[SalesDashboard] Response data:', (error as any).response.data);
      }
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // Aplicar filtros a las órdenes
  const filteredOrders = React.useMemo(() => {
    let filtered = [...orders];
    
    // Filtro por estado
    if (filterStatus !== 'all') {
      filtered = filtered.filter(o => o.status === filterStatus);
    }
    
    // Filtro por fecha desde
    if (filterDateFrom) {
      const fromDate = new Date(filterDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(o => {
        const orderDate = new Date(o.created_at);
        return orderDate >= fromDate;
      });
    }
    
    // Filtro por fecha hasta
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(o => {
        const orderDate = new Date(o.created_at);
        return orderDate <= toDate;
      });
    }
    
    console.log('[SalesDashboard] Filtros aplicados:', { filterStatus, filterDateFrom, filterDateTo });
    console.log('[SalesDashboard] Órdenes después de filtrar:', filtered.length, 'de', orders.length);
    
    return filtered;
  }, [orders, filterStatus, filterDateFrom, filterDateTo]);

  // Filtered Data - Usar filteredOrders en lugar de orders
  const productSales = React.useMemo(() => {
    console.log('[SalesDashboard] Filtrando datos. Orders totales:', filteredOrders.length);
    const sales = filteredOrders.filter(o => o.status !== 'cancelled');
    console.log('[SalesDashboard] Product sales (no cancelled):', sales.length);
    return sales;
  }, [filteredOrders]);

  const serviceSales = React.useMemo(() => 
    appointments.filter(a => a.status === 'COMPLETED'), 
    [appointments]
  );

  const deposits = React.useMemo(() => {
    // SOLO mostrar reservas con abono REAL (booking_source='online' y con depósito confirmado)
    // Las reservas manuales (admin) NO tienen abono, así que no deben aparecer aquí
    const filtered = appointments.filter(a => 
      a.status === 'CONFIRMED' && 
      (a.booking_source === 'online' || a.has_deposit === true)
    );
    // Ordenar por fecha descendente (más recientes primero)
    const sorted = filtered.sort((a, b) => {
      const dateA = new Date(a.date + 'T' + a.start_time);
      const dateB = new Date(b.date + 'T' + b.start_time);
      return dateB.getTime() - dateA.getTime();
    });
    console.log('[SalesDashboard] Filtrando abonos. Total appointments:', appointments.length);
    console.log('[SalesDashboard] Appointments con abono real (online + CONFIRMED):', filtered.length);
    console.log('[SalesDashboard] Appointments con abono (ordenados):', sorted);
    return sorted;
  }, [appointments]);

  // Calculations - Recalcular automáticamente cuando cambian los datos filtrados
  const totalProductSales = React.useMemo(() => 
    productSales.reduce((sum, order) => sum + Number(order.total_amount), 0),
    [productSales]
  );

  const totalServiceSales = React.useMemo(() => 
    serviceSales.reduce((sum, appt) => sum + Number(appt.total_price), 0),
    [serviceSales]
  );

  const totalDeposits = React.useMemo(() => 
    deposits.reduce((sum, appt) => sum + (appt.deposit_amount || Number(appt.total_price) / 2), 0),
    [deposits]
  );

  // Paginación para órdenes activas - usar filteredOrders
  const paginatedOrders = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  // Paginación para órdenes eliminadas
  const paginatedDeletedOrders = React.useMemo(() => {
    const startIndex = (deletedCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return deletedOrders.slice(startIndex, endIndex);
  }, [deletedOrders, deletedCurrentPage, itemsPerPage]);

  const totalDeletedPages = Math.ceil(deletedOrders.length / itemsPerPage);

  const handleViewOrderDetails = async (order: Order) => {
    try {
      setLoadingOrderDetails(true);
      console.log('[SalesDashboard] Cargando detalles de orden:', order.id);
      const fullOrder = await orderService.getById(order.id);
      console.log('[SalesDashboard] Orden completa recibida:', fullOrder);
      setSelectedOrder(fullOrder);
    } catch (error) {
      console.error('[SalesDashboard] Error cargando detalles:', error);
      setNotification({ type: 'error', message: 'Error al cargar los detalles del pedido' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Cargando datos de ventas...</div>;
  }

  const handleChangeStatus = async (orderId: string, newStatus: Order['status']) => {
    try {
      setChangingStatusId(orderId);
      console.log('[SalesDashboard] Cambiando estado de orden', orderId, 'a', newStatus);
      
      // Actualización optimista: actualizar el estado local inmediatamente
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      
      // Luego hacer el request al backend
      const updatedOrder = await orderService.updateStatus(orderId, newStatus);
      console.log('[SalesDashboard] Backend respondió con orden actualizada:', updatedOrder);
      
      // Actualizar con los datos reales del backend
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? updatedOrder : order
        )
      );
      
      // Mostrar notificación de éxito
      setNotification({ type: 'success', message: 'Estado actualizado con éxito' });
      setTimeout(() => setNotification(null), 3000);
      
    } catch (e) {
      console.error('[SalesDashboard] Error cambiando estado:', e);
      
      // Revertir el cambio optimista recargando del servidor
      setReloadingData(true);
      await loadData(false);
      setReloadingData(false);
      
      setNotification({ type: 'error', message: 'Error al cambiar el estado' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setChangingStatusId(null);
    }
  };

  const handleDelete = async (orderId: string) => {
    // Mostrar modal de confirmación personalizado
    setOrderToDelete(orderId);
  };

  const confirmDelete = async () => {
    if (!orderToDelete) {
      console.warn('[SalesDashboard] confirmDelete llamado sin orderToDelete');
      return;
    }
    
    const orderIdToDelete = orderToDelete;
    console.log('[SalesDashboard] Iniciando eliminación de orden:', orderIdToDelete);
    
    try {
      setDeletingId(orderIdToDelete);
      setOrderToDelete(null); // Cerrar el modal inmediatamente
      
      // Realizar la eliminación
      await orderService.delete(orderIdToDelete);
      console.log('[SalesDashboard] Orden eliminada exitosamente:', orderIdToDelete);
      
      // Eliminar localmente primero para feedback inmediato
      setOrders(prevOrders => {
        const filtered = prevOrders.filter(o => o.id !== orderIdToDelete);
        console.log('[SalesDashboard] Órdenes después de filtrar:', filtered.length);
        return filtered;
      });
      
      // Mostrar notificación de éxito
      setNotification({ type: 'success', message: 'Venta eliminada con éxito' });
      setTimeout(() => setNotification(null), 3000);
      
      // Recargar datos del servidor para asegurar sincronización
      setReloadingData(true);
      try {
        await loadData(false);
        console.log('[SalesDashboard] Datos recargados después de eliminar');
      } catch (reloadError) {
        console.error('[SalesDashboard] Error recargando datos:', reloadError);
      } finally {
        setReloadingData(false);
      }
    } catch (e) {
      console.error('[SalesDashboard] Error eliminando pedido:', e);
      const errorMessage = (e as any)?.response?.data?.detail || 'Error al eliminar la venta';
      setNotification({ type: 'error', message: errorMessage });
      setTimeout(() => setNotification(null), 3000);
      
      // Si falla, recargar para asegurar que los datos sean correctos
      try {
        await loadData(false);
      } catch (reloadError) {
        console.error('[SalesDashboard] Error recargando datos después de fallo:', reloadError);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setOrderToDelete(null);
  };

  const handleRestore = async (orderId: string) => {
    try {
      setChangingStatusId(orderId);
      await orderService.restore(orderId);
      
      setNotification({ type: 'success', message: 'Venta restaurada con éxito' });
      setTimeout(() => setNotification(null), 3000);
      
      setReloadingData(true);
      await loadData(false);
      setReloadingData(false);
    } catch (e) {
      console.error('[SalesDashboard] Error restaurando:', e);
      setNotification({ type: 'error', message: 'Error al restaurar la venta' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setChangingStatusId(null);
    }
  };

  const handleEmptyTrash = async () => {
    try {
      setReloadingData(true);
      await orderService.emptyTrash();
      
      setNotification({ type: 'success', message: 'Papelera vaciada exitosamente' });
      setTimeout(() => setNotification(null), 3000);
      
      setShowEmptyTrashModal(false);
      await loadData(false);
      setReloadingData(false);
    } catch (e) {
      console.error('[SalesDashboard] Error vaciando papelera:', e);
      setNotification({ type: 'error', message: 'Error al vaciar la papelera' });
      setTimeout(() => setNotification(null), 3000);
      setReloadingData(false);
      setShowEmptyTrashModal(false);
    }
  };

  // Helper functions para estilos de estado
  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-purple-100 text-purple-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status: Order['status']) => {
    const statusLabels: Record<Order['status'], string> = {
      'pending': 'Pendiente',
      'processing': 'Procesando',
      'shipped': 'Enviado',
      'delivered': 'Entregado',
      'cancelled': 'Cancelado'
    };
    return statusLabels[status] || status;
  };

  return (
    <div className="space-y-6">
      {/* Notification Popup */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className={`flex items-center space-x-3 px-6 py-4 rounded-lg shadow-lg ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {reloadingData && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
              <p className="text-gray-700 font-medium">Actualizando datos...</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {orderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
            <div className="flex flex-col items-center text-center space-y-4">
              {/* Icon */}
              <div className="w-16 h-16 bg-red-500 bg-opacity-20 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              
              {/* Title */}
              <h3 className="text-2xl font-bold text-white">
                Acción Permanente
              </h3>
              
              {/* Message */}
              <p className="text-gray-300 text-base leading-relaxed">
                ¿Estás seguro de que deseas eliminar esta venta? Esta acción no se puede deshacer.
              </p>
              
              {/* Buttons */}
              <div className="flex items-center gap-4 w-full pt-4">
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Aceptar
                </button>
                <button
                  onClick={cancelDelete}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-200 font-semibold py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty Trash Confirmation Modal */}
      {showEmptyTrashModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
            <div className="flex flex-col items-center text-center space-y-4">
              {/* Icon */}
              <div className="w-16 h-16 bg-red-500 bg-opacity-20 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              
              {/* Title */}
              <h3 className="text-2xl font-bold text-white">
                ⚠️ ACCIÓN PERMANENTE
              </h3>
              
              {/* Message */}
              <div className="text-gray-300 text-base leading-relaxed space-y-2">
                <p className="font-semibold">
                  ¿Estás seguro de que deseas vaciar la papelera?
                </p>
                <p className="text-sm">
                  Se eliminarán permanentemente <span className="text-red-400 font-bold">{deletedOrders.length} ventas</span>.
                </p>
                <p className="text-red-400 text-xs font-semibold">
                  Esta acción NO se puede deshacer.
                </p>
              </div>
              
              {/* Buttons */}
              <div className="flex items-center gap-4 w-full pt-4">
                <button
                  onClick={handleEmptyTrash}
                  disabled={reloadingData}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reloadingData ? 'Vaciando...' : 'Sí, Vaciar'}
                </button>
                <button
                  onClick={() => setShowEmptyTrashModal(false)}
                  disabled={reloadingData}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-200 font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub-tabs Navigation */}
      <div className="flex space-x-2 border-b border-gray-200 pb-1 overflow-x-auto">
        <button
          onClick={() => setActiveTab('products')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center space-x-2 ${
            activeTab === 'products'
              ? 'bg-white border-l border-t border-r border-gray-200 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ShoppingBag className="w-4 h-4" />
          <span>Ventas de Productos</span>
        </button>
        <button
          onClick={() => setActiveTab('services')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center space-x-2 ${
            activeTab === 'services'
              ? 'bg-white border-l border-t border-r border-gray-200 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>Ventas Servicios</span>
        </button>
        <button
          onClick={() => setActiveTab('deposits')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center space-x-2 ${
            activeTab === 'deposits'
              ? 'bg-white border-l border-t border-r border-gray-200 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          <span>Abonos</span>
        </button>
        <button
          onClick={() => setActiveTab('subscriptions')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center space-x-2 ${
            activeTab === 'subscriptions'
              ? 'bg-white border-l border-t border-r border-gray-200 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Package className="w-4 h-4" />
          <span>Suscripciones</span>
        </button>
        <button
          onClick={() => {
            setActiveTab('deleted');
            setDeletedCurrentPage(1);
          }}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center space-x-2 ${
            activeTab === 'deleted'
              ? 'bg-white border-l border-t border-r border-gray-200 text-red-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <XCircle className="w-4 h-4" />
          <span>Ventas Eliminadas</span>
          {deletedOrders.length > 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
              {deletedOrders.length}
            </span>
          )}
        </button>
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-b-lg shadow-sm border border-gray-200 border-t-0 p-6">
        
        {/* PRODUCT SALES */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-600 font-medium">Total Ventas</p>
                <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalProductSales)}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <p className="text-sm text-green-600 font-medium">Pedidos Exitosos</p>
                <p className="text-2xl font-bold text-green-900">{productSales.length}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <p className="text-sm text-gray-600 font-medium">Ticket Promedio</p>
                <p className="text-2xl font-bold text-gray-900">
                  {productSales.length ? formatCurrency(totalProductSales / productSales.length) : formatCurrency(0)}
                </p>
              </div>
            </div>

            {/* Filtros */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 mb-4">
                <Filter className="w-5 h-5 text-gray-600" />
                <h3 className="text-sm font-semibold text-gray-700">Filtrar Ventas</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as Order['status'] | 'all')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="pending">Pendiente</option>
                    <option value="processing">Procesando</option>
                    <option value="shipped">Enviado</option>
                    <option value="delivered">Entregado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Desde
                  </label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hasta
                  </label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setFilterStatus('all');
                      setFilterDateFrom('');
                      setFilterDateTo('');
                    }}
                    className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors text-sm"
                  >
                    Limpiar Filtros
                  </button>
                </div>
              </div>
              
              {/* Indicador de filtros activos */}
              {(filterStatus !== 'all' || filterDateFrom || filterDateTo) && (
                <div className="mt-3 flex items-center space-x-2 text-sm text-gray-600">
                  <span className="font-medium">Filtros activos:</span>
                  {filterStatus !== 'all' && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      Estado: {filterStatus}
                    </span>
                  )}
                  {filterDateFrom && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                      Desde: {new Date(filterDateFrom).toLocaleDateString('es-ES')}
                    </span>
                  )}
                  {filterDateTo && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                      Hasta: {new Date(filterDateTo).toLocaleDateString('es-ES')}
                    </span>
                  )}
                  <span className="text-gray-500">
                    ({filteredOrders.length} de {orders.length} órdenes)
                  </span>
                </div>
              )}
            </div>

            {/* Tabla con scroll en mobile */}
            <div className="overflow-x-auto -mx-6 px-6">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-6">Código</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">ID Ref.</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Fecha</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Cliente</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Estado</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Total</th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Acciones</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {paginatedOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {order.public_code || '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                            <div className="flex items-center space-x-1">
                              <span className="font-mono">#{order.id.slice(0, 8)}</span>
                              <button
                                className="p-1 text-xs hover:bg-gray-100 rounded"
                                onClick={() => {
                                  navigator.clipboard.writeText(order.id);
                                  setNotification({ type: 'success', message: 'ID copiado' });
                                  setTimeout(() => setNotification(null), 2000);
                                }}
                                title="Copiar UUID completo"
                              >
                                📋
                              </button>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString('es-ES')}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                            <div className="truncate" title={(order as any).customer_name || order.email || 'Invitado'}>
                              {(order as any).customer_name || order.email || 'Invitado'}
                            </div>
                          </td>
                          <td className="px-3 py-4">
                            <div className="flex flex-col space-y-2 min-w-[200px]">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit
                                ${order.status === 'delivered' ? 'bg-green-100 text-green-800' : 
                                  order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                                  order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                                  order.status === 'processing' ? 'bg-purple-100 text-purple-800' :
                                  'bg-yellow-100 text-yellow-800'}`}>
                                {order.status}
                              </span>
                              <div className="relative">
                                {changingStatusId === order.id && (
                                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded">
                                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                  </div>
                                )}
                                <select
                                  className="text-xs border border-gray-300 rounded px-2 py-1.5 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                                  value={order.status}
                                  onChange={(e) => handleChangeStatus(order.id, e.target.value as Order['status'])}
                                  disabled={changingStatusId === order.id || deletingId === order.id}
                                >
                                  <option value="pending">Pendiente</option>
                                  <option value="processing">Procesando</option>
                                  <option value="shipped">Enviado</option>
                                  <option value="delivered">Entregado</option>
                                  <option value="cancelled">Cancelado</option>
                                </select>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-semibold text-gray-900">
                            {formatCurrency(order.total_amount)}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex flex-col space-y-2 items-stretch">
                              <button
                                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                onClick={() => handleViewOrderDetails(order)}
                                disabled={deletingId === order.id || loadingOrderDetails}
                              >
                                {loadingOrderDetails ? 'Cargando...' : 'Ver detalles'}
                              </button>
                              <button
                                className="inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                onClick={() => handleDelete(order.id)}
                                disabled={changingStatusId === order.id || deletingId === order.id}
                              >
                                {deletingId === order.id ? (
                                  <>
                                    <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                                    Eliminando...
                                  </>
                                ) : (
                                  'Eliminar'
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                          {paginatedOrders.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                            <div className="flex flex-col items-center space-y-2">
                              <Package className="w-12 h-12 text-gray-400" />
                              <p className="font-medium">No hay pedidos en esta página</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Pagination Controls */}
            {filteredOrders.length > itemsPerPage && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredOrders.length)}</span> de{' '}
                      <span className="font-medium">{filteredOrders.length}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Anterior</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                        </svg>
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i;
                        if (pageNum < 1 || pageNum > totalPages) return null;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                              currentPage === pageNum
                                ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Siguiente</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}

            {selectedOrder && (
              <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={() => setSelectedOrder(null)}>
                <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Detalle de Pedido</h3>
                    <button className="text-gray-500 hover:text-gray-700" onClick={() => setSelectedOrder(null)}>✕</button>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Código</p>
                        <p className="font-mono text-sm">{selectedOrder.public_code || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">ID Referencia</p>
                        <p className="font-mono text-sm break-all">{selectedOrder.id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Estado</p>
                        <p className="text-sm">{selectedOrder.status}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Fecha</p>
                        <p className="text-sm">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Nombre</p>
                        <p className="text-sm font-medium">{(selectedOrder as any).customer_name || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="text-sm">{selectedOrder.email || '—'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Teléfono</p>
                        <p className="text-sm">{(selectedOrder as any).phone || '—'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Dirección de Envío</p>
                      <p className="text-sm break-words">{(selectedOrder as any).shipping_address || '—'}</p>
                      {(selectedOrder as any).shipping_region && (
                        <p className="text-xs text-gray-500 mt-1">Región: {(selectedOrder as any).shipping_region}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Productos ({Array.isArray((selectedOrder as any).items) ? (selectedOrder as any).items.length : 0})</p>
                      <div className="border rounded max-h-80 overflow-y-auto">
                        {Array.isArray((selectedOrder as any).items) && (selectedOrder as any).items.length > 0 ? (
                          ((selectedOrder as any).items as Array<any>).map((it, idx) => (
                            <div key={idx} className="flex gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50">
                              {/* Imagen del producto */}
                              <div className="flex-shrink-0">
                                <img
                                  src={it.product?.image || '/placeholder-product.png'}
                                  alt={it.product?.name || 'Producto'}
                                  className="w-16 h-16 object-cover rounded border border-gray-200"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/placeholder-product.png';
                                  }}
                                />
                              </div>
                              
                              {/* Detalles del producto */}
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start mb-1">
                                  <h4 className="font-medium text-sm text-gray-900 truncate pr-2">
                                    {it.product?.name || it.product_name || 'Producto'}
                                  </h4>
                                  <span className="text-sm font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded flex-shrink-0">
                                    x{it.quantity || 1}
                                  </span>
                                </div>
                                
                                {/* Variante - Tono/Color */}
                                {it.product_variant && (
                                  <div className="space-y-1 mb-2">
                                    {/* Si el name es "Color" o "Tono" o tiene color_hex, mostrar como color */}
                                    {(it.product_variant.name?.toLowerCase().includes('color') || it.product_variant.name?.toLowerCase().includes('tono') || it.product_variant.color_hex) && (
                                      <div className="flex items-center gap-2">
                                        {it.product_variant.color_hex && (
                                          <div 
                                            className="w-6 h-6 rounded-full border-2 border-gray-300 shadow-sm flex-shrink-0" 
                                            style={{ backgroundColor: it.product_variant.color_hex }}
                                          />
                                        )}
                                        <span className="text-sm font-medium text-gray-700">
                                          {it.product_variant.name}
                                        </span>
                                      </div>
                                    )}
                                    {/* Para otras variantes (talla, etc.) */}
                                    {!it.product_variant.name?.toLowerCase().includes('color') && !it.product_variant.name?.toLowerCase().includes('tono') && !it.product_variant.color_hex && (
                                      <div className="text-xs text-gray-600">
                                        {it.product_variant.name}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Ubicación */}
                                {it.stock_location_display && (
                                  <div className="text-xs text-gray-500 mb-1">
                                    📍 Sede: {it.stock_location_display}
                                  </div>
                                )}
                                
                                {/* Precios */}
                                <div className="flex justify-between items-center text-xs mt-2">
                                  <span className="text-gray-500">
                                    {formatCurrency(it.unit_price)} c/u
                                  </span>
                                  <span className="font-semibold text-gray-900">
                                    Total: {formatCurrency(it.total_price)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-xs text-gray-500">
                            No hay productos en este pedido
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Resumen de Pago */}
                    <div className="border-t pt-3 mt-3">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Resumen de Pago</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal productos:</span>
                          <span className="font-medium">
                            {formatCurrency(
                              Array.isArray((selectedOrder as any).items) 
                                ? (selectedOrder as any).items.reduce((sum: number, it: any) => sum + (Number(it.total_price) || 0), 0)
                                : 0
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Costo de envío:</span>
                          <span className="font-medium">
                            {(selectedOrder as any).shipping_cost 
                              ? formatCurrency(Number((selectedOrder as any).shipping_cost))
                              : 'Gratis'
                            }
                          </span>
                        </div>
                        <div className="flex justify-between text-base font-semibold border-t pt-2 mt-2">
                          <span className="text-gray-900">Total:</span>
                          <span className="text-gray-900">{formatCurrency(Number(selectedOrder.total_amount))}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <button
                        className="px-3 py-2 text-sm border rounded hover:bg-gray-50"
                        onClick={() => setSelectedOrder(null)}
                      >Cerrar</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SERVICE SALES */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <p className="text-sm text-purple-600 font-medium">Total Facturado</p>
                <p className="text-2xl font-bold text-purple-900">{formatCurrency(totalServiceSales)}</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <p className="text-sm text-indigo-600 font-medium">Servicios Completados</p>
                <p className="text-2xl font-bold text-indigo-900">{serviceSales.length}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profesional</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serviceSales.map((appt) => (
                    <tr key={appt.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {appt.date} {appt.start_time}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {appt.customer_name || appt.customer_email || 'Cliente Registrado'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {appt.professional_detail ? `${appt.professional_detail.first_name} ${appt.professional_detail.last_name}` : 'Sin asignar'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(appt.total_price)}
                      </td>
                    </tr>
                  ))}
                  {serviceSales.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-gray-500">No hay servicios completados.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* DEPOSITS */}
        {activeTab === 'deposits' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100 mb-4">
              <div className="flex items-start">
                <TrendingUp className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Resumen de Abonos</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    Mostrando solo reservas online con abono pagado (50%). Las reservas manuales no aparecen aquí.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                <p className="text-sm text-orange-600 font-medium">Total en Abonos Cobrados</p>
                <p className="text-2xl font-bold text-orange-900">{formatCurrency(totalDeposits)}</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                <p className="text-sm text-amber-600 font-medium">Cantidad de Abonos</p>
                <p className="text-2xl font-bold text-amber-900">{deposits.length}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Reserva</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abono Pagado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deposits.map((appt) => (
                    <tr key={appt.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {appt.date} {appt.start_time}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {appt.customer_name || appt.customer_email || 'Sin datos de cliente'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Confirmada
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(appt.deposit_amount || Number(appt.total_price) / 2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(appt.total_price)}
                      </td>
                    </tr>
                  ))}
                  {deposits.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-gray-500">No hay abonos registrados.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* DELETED ORDERS */}
        {activeTab === 'deleted' && (
          <div className="space-y-6">
            {/* Header with Empty Trash Button */}
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">Papelera de Ventas</h3>
                <span className="text-sm text-gray-500">({deletedOrders.length} eliminados)</span>
              </div>
              {deletedOrders.length > 0 && (
                <button
                  onClick={() => setShowEmptyTrashModal(true)}
                  disabled={reloadingData}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reloadingData ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Vaciando...</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      <span>Vaciar Papelera</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {deletedOrders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No hay ventas eliminadas</h3>
                <p className="text-gray-500 mt-2">Las ventas eliminadas aparecerán aquí y podrás restaurarlas si lo necesitas.</p>
              </div>
            ) : (
              <>
                {/* Deleted Orders Table */}
                <div className="overflow-x-auto -mx-6 px-6">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-6">Código</th>
                            <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Cliente</th>
                            <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Monto</th>
                            <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Estado</th>
                            <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Eliminado</th>
                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                              <span className="sr-only">Acciones</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {paginatedDeletedOrders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-50 opacity-70">
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {order.order_code || `#${order.id}`}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                                {order.customer_name || order.email || 'Sin nombre'}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-medium">
                                {formatCurrency(order.total_price)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                                  {getStatusText(order.status)}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {order.deleted_at ? new Date(order.deleted_at).toLocaleDateString('es-ES', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : '-'}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <button
                                  onClick={() => handleRestore(order.id)}
                                  disabled={reloadingData}
                                  className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                >
                                  {reloadingData ? (
                                    <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <>
                                      <CheckCircle className="w-4 h-4" />
                                      <span>Restaurar</span>
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Pagination Controls for Deleted Orders */}
                {deletedOrders.length > itemsPerPage && (
                  <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={() => setDeletedCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={deletedCurrentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setDeletedCurrentPage(prev => Math.min(totalDeletedPages, prev + 1))}
                        disabled={deletedCurrentPage === totalDeletedPages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Mostrando <span className="font-medium">{(deletedCurrentPage - 1) * itemsPerPage + 1}</span> a{' '}
                          <span className="font-medium">{Math.min(deletedCurrentPage * itemsPerPage, deletedOrders.length)}</span> de{' '}
                          <span className="font-medium">{deletedOrders.length}</span> resultados
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => setDeletedCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={deletedCurrentPage === 1}
                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Anterior</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                            </svg>
                          </button>
                          {Array.from({ length: Math.min(5, totalDeletedPages) }, (_, i) => {
                            const pageNum = deletedCurrentPage <= 3 ? i + 1 : deletedCurrentPage >= totalDeletedPages - 2 ? totalDeletedPages - 4 + i : deletedCurrentPage - 2 + i;
                            if (pageNum < 1 || pageNum > totalDeletedPages) return null;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setDeletedCurrentPage(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                  deletedCurrentPage === pageNum
                                    ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => setDeletedCurrentPage(prev => Math.min(totalDeletedPages, prev + 1))}
                            disabled={deletedCurrentPage === totalDeletedPages}
                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Siguiente</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* SUBSCRIPTIONS */}
        {activeTab === 'subscriptions' && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Suscripciones y Planes</h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">
              Este módulo está en desarrollo. Próximamente podrás gestionar suscripciones recurrentes y planes de membresía para tus clientes.
            </p>
            <button className="mt-6 px-4 py-2 bg-gray-900 text-white rounded-lg opacity-50 cursor-not-allowed">
              Crear Plan (Próximamente)
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default SalesDashboard;
